import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { changeInstallmentLink } from '../../pages/CAPage/utils';

const LOCALE_PREFIX = 'GB/pages/CAPage/CreditDetailsPopup';

export const RiskBasedPricingInstallment = ({
    creditLine,
    currencySymbol,
    flow,
    grossTotalValue,
    installmentCount,
    installmentMonthlyPayment,
    installmentApr,
    installmentTerm,
    purchaseApr,
    showCYCO
}) => (
    <article className="CreditDetailsArticle" data-testid="rbp-installment">
        <div className="CreditArticleContainer CreditArticleContainer--installment">
            <div className="CreditDetailsContainer-article-header vx_text-6">
                <FormattedMessage id={`${LOCALE_PREFIX}.rbpCreditLimit`} />
            </div>
            <div className="CreditDetailsContainer-article-highlight">{creditLine}</div>
        </div>
        <div className="CreditArticleContainer CreditArticleContainer--installment">
            <div className="CreditDetailsContainer-article-header vx_text-6">
                <FormattedMessage
                    id={`${LOCALE_PREFIX}.rbpCreditLimitHeader`}
                    values={{
                        symbol: currencySymbol,
                        total: grossTotalValue
                    }}
                />
            </div>
            <div className="CreditDetailsContainer-article-highlight">{installmentTerm} payments of {installmentMonthlyPayment}</div>
            <div className="CreditDetailsContainer-article-details vx_text-body-sm">
                <span className="CreditDetailsContainer-article-details-left">
                    <FormattedMessage
                        id={`${LOCALE_PREFIX}.rbpCreditLimitRate`}
                        values={{
                            rate: installmentApr,
                            installmentDuration: installmentTerm
                        }}
                    />
                </span>
                <span>{changeInstallmentLink({ flow, installmentCount, showCYCO })}</span>
            </div>
        </div>
        <div className="CreditArticleContainer CreditArticleContainer--installment">
            <div className="CreditDetailsContainer-article-header vx_text-6">
                <FormattedMessage id={`${LOCALE_PREFIX}.standardRateHeader`} />
            </div>
            <div className="CreditDetailsContainer-article-highlight">{purchaseApr}% p.a. (variable)</div>
        </div>
    </article>
);

RiskBasedPricingInstallment.propTypes = {
    creditLine: PropTypes.string,
    purchaseApr: PropTypes.string,
    currencySymbol: PropTypes.string,
    grossTotalValue: PropTypes.string,
    installmentApr: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    installmentTerm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    installmentMonthlyPayment: PropTypes.string,
    flow: PropTypes.string,
    installmentCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showCYCO: PropTypes.bool
};

export const RiskBasedPricingNI = ({
    creditLine,
    currencySymbol,
    flow,
    grossTotalValue,
    installmentApr,
    installmentTerm,
    installmentCount,
    showCYCO,
    purchaseApr,
}) => {
    const change = changeInstallmentLink({ flow, installmentCount, showCYCO });
    return (
        <article className="CreditDetailsArticle" data-testid="rbp-noInterest">
            <div className="CreditArticleContainer CreditArticleContainer--noInterest">
                <div className="CreditDetailsContainer-article-header vx_text-6">
                    <FormattedMessage id={`${LOCALE_PREFIX}.rbpCreditLimit`} />
                </div>
                <div className="CreditDetailsContainer-article-highlight">{creditLine}</div>
            </div>
            <div className="CreditArticleContainer CreditArticleContainer--noInterest">
                <div className="CreditDetailsContainer-article-header vx_text-6">
                    <FormattedMessage
                        id={`${LOCALE_PREFIX}.rbpCreditLimitHeader`}
                        values={{
                            symbol: currencySymbol,
                            total: grossTotalValue
                        }}
                    />
                </div>
                <div className="CreditDetailsContainer-article-highlight CreditArticleContainer--noInterest">{installmentApr}% p.a. for {installmentTerm} months</div>
                { change && <div className="CreditDetailsContainer-article-details vx_text-body-sm">{change}</div> }
            </div>
            <div className="CreditArticleContainer CreditArticleContainer--noInterest">
                <div className="CreditDetailsContainer-article-header vx_text-6">
                    <FormattedMessage id={`${LOCALE_PREFIX}.standardRateHeader`} />
                </div>
                <div className="CreditDetailsContainer-article-highlight">{purchaseApr}% p.a. (variable)</div>
            </div>
        </article>
    );
};

RiskBasedPricingNI.propTypes = {
    creditLine: PropTypes.string,
    purchaseApr: PropTypes.string,
    currencySymbol: PropTypes.string,
    grossTotalValue: PropTypes.string,
    installmentApr: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    installmentTerm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    flow: PropTypes.string,
    installmentCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showCYCO: PropTypes.bool
};

export const RiskBasedPricingCreditLimit = ({ creditLine, purchaseApr }) => (
    <article className="CreditDetailsArticle" data-testid="rbp-creditLimit">
        <div className="CreditArticleContainer CreditArticleContainer--creditLimit">
            <div className="CreditDetailsContainer-article-header vx_text-6">
                <FormattedMessage id={`${LOCALE_PREFIX}.rbpCreditLimit`} />
            </div>
            <div className="CreditDetailsContainer-article-highlight">{creditLine}</div>
        </div>
        <div className="CreditArticleContainer CreditArticleContainer--creditLimit">
            <div className="CreditDetailsContainer-article-header vx_text-6">
                <FormattedMessage id={`${LOCALE_PREFIX}.standardRateHeader`} />
            </div>
            <div className="CreditDetailsContainer-article-highlight">{purchaseApr}% p.a. (variable)</div>
        </div>
    </article>
);

RiskBasedPricingCreditLimit.propTypes = {
    creditLine: PropTypes.string.isRequired,
    purchaseApr: PropTypes.string.isRequired
};

export const mapStateToProps = state => ({
    creditLine: _.get(state, 'serverData.decisionModel.creditLine.format'),
    purchaseApr: _.get(state, 'serverData.decisionModel.purchaseApr'),
    currencySymbol: _.get(state, 'serverData.currencySymbol'),
    flow: _.get(state, 'serverData.flow'),
    grossTotalValue: _.get(state, 'serverData.cart.gross_total_amount.value'),
    installmentApr: _.get(state, 'serverData.financingDetails.apr', 0),
    installmentCount: _.get(state, 'serverData.installmentCount', -1),
    installmentMonthlyPayment: _.get(state, 'serverData.financingDetails.monthly_payment'),
    installmentTerm: _.get(state, 'serverData.financingDetails.term'),
    showCYCO: _.get(state, 'serverData.showCYCO'),
});

export const RBPCreditLimit = connect(mapStateToProps)(RiskBasedPricingCreditLimit);
export const RBPNoInterest = connect(mapStateToProps)(RiskBasedPricingNI);
export const RBPInstallment = connect(mapStateToProps)(RiskBasedPricingInstallment);
